import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Truck, Search, MapPin, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Host-Hostes Firması", "Ses - Işık Firması", "Catering", "Transfer",
  "Seyahat Acentası", "Baskıcı", "Atölye", "Kurulum Ekibi", "Otel-Konaklama",
  "Fuar Lojistik", "Promosyon", "Bez-Kağıt Çanta", "Plaket", "Tasarım Ajansı",
  "Güvenlik", "Mobilya", "Ambulans", "Montaj", "Event Ajansı"
];

export default function SuppliersListPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async (cat = "", q = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat) params.set("category", cat);
      if (q) params.set("search", q);
      const res = await axios.get(`${API}/suppliers?${params}`);
      setSuppliers(res.data);
    } catch {
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadSuppliers(selectedCategory, search);
  };

  const handleCategoryClick = (cat) => {
    const newCat = selectedCategory === cat ? "" : cat;
    setSelectedCategory(newCat);
    loadSuppliers(newCat, search);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero */}
      <div className="bg-[#1B4F72] text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-3">Fuar Tedarikçileri</h1>
          <p className="text-white/80 max-w-xl mx-auto">
            Catering, ses ışık, hostes, transfer, lojistik ve daha fazlası. Fuar projeleriniz için güvenilir tedarikçiler.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6 max-w-md mx-auto">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Şehir ara..."
            className="flex-1"
          />
          <Button type="submit" className="bg-[#1B4F72] hover:bg-[#154360] gap-1.5">
            <Search className="w-4 h-4" /> Ara
          </Button>
        </form>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => handleCategoryClick("")}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              selectedCategory === ""
                ? "bg-[#1B4F72] text-white border-[#1B4F72]"
                : "bg-white text-stone-600 border-stone-200 hover:border-[#1B4F72]"
            }`}
          >
            Tümü
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryClick(cat)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                selectedCategory === cat
                  ? "bg-[#1B4F72] text-white border-[#1B4F72]"
                  : "bg-white text-stone-600 border-stone-200 hover:border-[#1B4F72]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-stone-300" />
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16">
            <Truck className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Tedarikçi Bulunamadı</h2>
            <p className="text-stone-500 mb-6">Bu kriterlere uygun tedarikçi henüz kayıt olmamış.</p>
            <Link to="/tedarikci-giris">
              <Button className="bg-[#1B4F72] hover:bg-[#154360]">Tedarikçi Olarak Kayıt Ol</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliers.map((supplier) => (
              <Link
                key={supplier.slug}
                to={`/tedarikci/${supplier.slug}`}
                className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md hover:border-[#1B4F72]/30 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {supplier.logo_url ? (
                      <img src={supplier.logo_url} alt="" className="w-full h-full object-contain p-1" />
                    ) : (
                      <Truck className="w-6 h-6 text-stone-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-stone-900 group-hover:text-[#1B4F72] transition-colors truncate">
                      {supplier.company_name}
                    </h3>
                    {supplier.city && (
                      <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {supplier.city}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(supplier.categories || [supplier.category]).filter(Boolean).slice(0, 2).map((cat, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{cat}</Badge>
                      ))}
                    </div>
                    {supplier.description && (
                      <p className="text-xs text-stone-500 mt-2 line-clamp-2">{supplier.description}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[#1B4F72] text-white rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold font-serif mb-2">Siz de Tedarikçi Olun</h2>
          <p className="text-white/80 mb-4">Hizmetlerinizi fuar sektörüne tanıtın, yeni müşterilere ulaşın.</p>
          <Link to="/tedarikci-giris">
            <Button variant="secondary" className="bg-white text-[#1B4F72] hover:bg-stone-100">
              KAYIT OL
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
